import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { MessageSquare, X, Send, Trash2, Zap, User, Bot, Loader2 } from 'lucide-react';
import { completeWithGACA, GACAError } from '../services/gacaClient';
import { useChatHistory, ChatMessage } from '../hooks/useChatHistory';
import { useGACAStatus } from '../hooks/useGACAStatus';

const BIZNESFINDER_SYSTEM_PROMPT = `Jesteś specjalistą ds. pozyskiwania leadów i kontaktów biznesowych dla BiznesFinder AI.

Twoje główne zadania:
- Pomagaj użytkownikom w wyszukiwaniu kontaktów do firm
- Doradzaj jak skutecznie targetować potencjalnych klientów
- Wyjaśniaj jak działa BiznesFinder AI
- Odpowiadaj na pytania o dane kontaktowe, branże, lokalizacje

Styl: profesjonalny, konkretny, pomocny. Pisz po polsku. Bez formatowania markdown — bez gwiazdek, bez nagłówków, bez list z myślnikami.`;

const GENERAL_SYSTEM_PROMPT = `Jesteś asystentem AI dla BiznesBezKlikania.pl - platformy oferującej narzędzia do automatyzacji biznesu z wykorzystaniem sztucznej inteligencji.

Twoje główne zadania:
- Odpowiadaj na pytania dotyczące produktów i usług BiznesBezKlikania
- Pomagaj w doborze odpowiednich narzędzi AI do potrzeb użytkownika
- Wyjaśniaj jak działa automatyzacja procesów biznesowych
- Udzielaj informacji o cenach i dostępnych pakietach

Produkty: BiznesFinder AI, Agent WhatsApp AI, SmartClaim AI, ContentFlow AI, Visual Generator AI, i inne.

Styl: profesjonalny ale przyjaźny, konkretny, pomocny. Pisz po polsku. Bez formatowania markdown — bez gwiazdek, bez nagłówków, bez list z myślnikami.`;

export const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const location = ReactRouterDOM.useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { status } = useGACAStatus({ pollInterval: 60000 });

  const BIZNESFINDER_PATH = 'biznesfinder-ai';
  const isBiznesFinderPage = location.pathname.includes(BIZNESFINDER_PATH);

  const storageKey = isBiznesFinderPage
    ? 'biznesfinder_chat_history'
    : 'biznesbezklikania_chat_history';

  const {
    messages,
    addMessage,
    clearHistory,
    isLoaded,
  } = useChatHistory({ storageKey, maxMessages: 50 });

  const systemPrompt = isBiznesFinderPage ? BIZNESFINDER_SYSTEM_PROMPT : GENERAL_SYSTEM_PROMPT;
  const title = isBiznesFinderPage ? 'Znajdź kontakty do firm' : 'Asystent AI';

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus management
  useEffect(() => {
    if (!isOpen) {
      triggerRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    setError(null);
    setInput('');
    setIsLoading(true);

    // Add user message
    addMessage({
      role: 'user',
      content: trimmedInput,
    });

    try {
      // Build conversation context (last 10 messages)
      const recentMessages = messages.slice(-10);
      const conversationContext = recentMessages
        .map((m) => `${m.role === 'user' ? 'Użytkownik' : 'Asystent'}: ${m.content}`)
        .join('\n');

      const fullPrompt = conversationContext
        ? `${conversationContext}\nUżytkownik: ${trimmedInput}\nAsystent:`
        : trimmedInput;

      const response = await completeWithGACA({
        prompt: fullPrompt,
        systemPrompt,
        maxTokens: 1024,
        temperature: 0.7,
      });

      addMessage({
        role: 'assistant',
        content: response.content,
        model: response.model,
        provider: response.provider,
      });
    } catch (err) {
      const errorMessage =
        err instanceof GACAError
          ? `Błąd: ${err.message}`
          : 'Przepraszam, wystąpił błąd. Spróbuj ponownie.';

      setError(errorMessage);

      addMessage({
        role: 'assistant',
        content: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, addMessage, systemPrompt]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getStatusColor = () => {
    if (!status || status.status === 'down') return 'bg-red-500';
    if (status.status === 'degraded') return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          fixed bottom-28 sm:bottom-20 right-6 w-16 h-16
          bg-gradient-to-r from-primary-light to-secondary-light
          dark:from-primary-dark dark:to-secondary-dark
          text-white rounded-full shadow-lg
          flex items-center justify-center
          transform hover:scale-110 transition-all duration-300
          z-[10000]
          ${isOpen ? '' : 'animate-glow'}
        `}
        aria-label={isOpen ? 'Zamknij czat' : 'Otwórz czat'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X className="w-8 h-8" /> : <MessageSquare className="w-8 h-8" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          className={`
            fixed bottom-[12rem] sm:bottom-40 right-6
            w-[90vw] max-w-md h-[70vh] max-h-[600px]
            bg-white/80 dark:bg-content-dark/90
            backdrop-blur-xl
            shadow-2xl rounded-2xl
            flex flex-col
            z-[10001] overflow-hidden
            origin-bottom-right animate-scale-in
            border border-white/20 dark:border-white/10
          `}
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-widget-title"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark p-4 text-white">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <h3 id="chat-widget-title" className="font-bold text-lg">
                    {title}
                  </h3>
                  <div className="flex items-center gap-1.5 text-xs text-white/80">
                    <span className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                    <span>G.A.C.A. AI</span>
                    {status && (
                      <span className="opacity-70">
                        ({status.activeProviders} providerów)
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearHistory}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  aria-label="Wyczyść historię"
                  title="Wyczyść historię"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  aria-label="Zamknij czat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-grow overflow-y-auto p-4 space-y-4">
            {!isLoaded ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-primary-light dark:text-primary-dark" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-text-secondary-light dark:text-text-secondary-dark py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center">
                  <MessageSquare className="w-8 h-8 text-primary-light dark:text-primary-dark" />
                </div>
                <p className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                  Cześć! Jak mogę Ci pomóc?
                </p>
                <p className="text-sm">
                  {isBiznesFinderPage
                    ? 'Zapytaj mnie o wyszukiwanie kontaktów biznesowych.'
                    : 'Zapytaj mnie o automatyzację biznesu z AI.'}
                </p>
              </div>
            ) : (
              messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))
            )}

            {/* Typing indicator */}
            {isLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-secondary-light/20 dark:bg-secondary-dark/20 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-secondary-light dark:text-secondary-dark" />
                </div>
                <div className="bg-white/50 dark:bg-white/10 backdrop-blur-sm rounded-2xl rounded-tl-none px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-text-secondary-light dark:bg-text-secondary-dark rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-text-secondary-light dark:bg-text-secondary-dark rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-text-secondary-light dark:bg-text-secondary-dark rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error display */}
          {error && (
            <div className="px-4 py-2 bg-red-500/10 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-border-light dark:border-border-dark">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Napisz wiadomość..."
                className={`
                  flex-grow resize-none rounded-xl px-4 py-3
                  bg-white/50 dark:bg-white/10
                  border border-border-light dark:border-border-dark
                  text-text-primary-light dark:text-text-primary-dark
                  placeholder-text-secondary-light dark:placeholder-text-secondary-dark
                  focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark
                  transition-all
                `}
                rows={1}
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className={`
                  p-3 rounded-xl
                  bg-primary-light dark:bg-primary-dark text-white
                  hover:opacity-90 transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center
                `}
                aria-label="Wyślij wiadomość"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-2 text-center">
              Powered by G.A.C.A. Multi-Provider AI
            </p>
          </form>
        </div>
      )}

      <style>{`
        @keyframes scale-in {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
      `}</style>
    </>
  );
};

interface MessageBubbleProps {
  message: ChatMessage;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`
          w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
          ${isUser
            ? 'bg-primary-light/20 dark:bg-primary-dark/20'
            : 'bg-secondary-light/20 dark:bg-secondary-dark/20'
          }
        `}
      >
        {isUser ? (
          <User className="w-4 h-4 text-primary-light dark:text-primary-dark" />
        ) : (
          <Bot className="w-4 h-4 text-secondary-light dark:text-secondary-dark" />
        )}
      </div>
      <div
        className={`
          max-w-[80%] rounded-2xl px-4 py-3
          ${isUser
            ? 'bg-primary-light dark:bg-primary-dark text-white rounded-tr-none'
            : 'bg-white/50 dark:bg-white/10 backdrop-blur-sm text-text-primary-light dark:text-text-primary-dark rounded-tl-none'
          }
        `}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
      </div>
    </div>
  );
};

export default ChatWidget;
